// src/components/Pages/Children.js
import React, { useMemo, useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Search,
  Eye,
  Pencil,
  UserPlus,
  MapPin,
  User,
  Calendar,
  Heart,
  FileText,
  Home,
} from "lucide-react";
import Button from "../UI/Button";
import {
  AddChildModal,
  EditProfileModal,
  ReintegrationModal,
  getStatusColor,
  getHealthStatusColor,
  getAdoptionStatusColor,
} from "../Modals/ChildrenModals";
import DevelopmentSummaryModal from "../Modals/DevelopmentSummaryModal";

/* ---------------- Avatar helpers (NO AI images) ---------------- */
const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || "" : "";
  return (first + last).toUpperCase();
};

const isValidPhoto = (v) => {
  if (!v) return false;
  const s = String(v).trim();
  if (!s || s === "null" || s === "undefined") return false;
  if (s.includes("pravatar.cc")) return false;
  return true;
};

const ChildAvatar = ({ fullName, photoUrl }) => {
  const initials = getInitials(fullName);
  const [broken, setBroken] = useState(false);

  useEffect(() => setBroken(false), [photoUrl]);

  const canShowPhoto = isValidPhoto(photoUrl) && !broken;

  if (canShowPhoto) {
    return (
      <img
        src={photoUrl}
        alt={fullName}
        className="w-12 h-12 rounded-full object-cover border border-gray-200 dark:border-gray-700 shrink-0"
        onError={() => setBroken(true)}
        referrerPolicy="no-referrer"
        loading="lazy"
      />
    );
  }

  return (
    <div
      className="w-12 h-12 rounded-full flex items-center justify-center shrink-0
                 border border-gray-200 dark:border-gray-700
                 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold"
      title={fullName}
    >
      {initials || <User size={18} />}
    </div>
  );
};

/* ---------------- Date helpers ---------------- */
const pad2 = (n) => String(n).padStart(2, "0");

const toDateInputValue = (value) => {
  if (!value) return "";
  try {
    if (value instanceof Date) {
      return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
    }

    const s = String(value).trim();
    if (!s) return "";

    if (s.includes("-") && s.split("-").length >= 3) {
      const [yy, mm, dd] = s.split("-");
      if (yy.length === 4) return `${yy}-${pad2(mm)}-${pad2(dd)}`;
    }

    if (s.includes("/") && s.split("/").length === 3) {
      const [m, d, y] = s.split("/");
      if (String(y).length === 4) return `${y}-${pad2(m)}-${pad2(d)}`;
    }

    const dt = new Date(s);
    if (Number.isNaN(dt.getTime())) return "";
    return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
  } catch {
    return "";
  }
};

const calculateAge = (dateOfBirth) => {
  const iso = toDateInputValue(dateOfBirth);
  if (!iso) return "";

  const birth = new Date(iso);
  if (Number.isNaN(birth.getTime())) return "";

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age >= 0 ? age : "";
};

const formatShortDate = (value) => {
  if (!value) return "—";

  const iso = toDateInputValue(value);
  if (!iso) return String(value);

  const [year, month, day] = iso.split("-");

  return `${month}/${day}/${year}`;
};

const Children = () => {
  const CHILDREN_ROUTE = "/children";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [children, setChildren] = useState([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [editChild, setEditChild] = useState(null);
  const [reintegrationChild, setReintegrationChild] = useState(null);
  const [devChild, setDevChild] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";
  const API_URL = `${API_BASE}/api/children`;

  const token = localStorage.getItem("admin_token");

  const getErrMsg = (data, fallback) => data?.message || data?.error || data?.details || fallback;

  const normalizePhoto = useCallback(
    (c) => {
      const raw = c?.photoUrl ?? c?.photo_url ?? c?.photo ?? null;
      if (!raw) return null;

      const s = String(raw).trim();
      if (!s || s === "null" || s === "undefined") return null;
      if (s.includes("pravatar.cc")) return null;

      if (/^https?:\/\//i.test(s)) return s;
      if (s.startsWith("/uploads/")) return `${API_BASE}${s}`;
      if (s.startsWith("uploads/")) return `${API_BASE}/${s}`;

      return `${API_BASE}/uploads/${s.replace(/^\/+/, "")}`;
    },
    [API_BASE]
  );

  useEffect(() => {
    const fetchChildren = async () => {
      try {
        const res = await fetch(API_URL, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.success === false) {
          console.error("Fetch children failed:", data);
          return;
        }

        const list = Array.isArray(data) ? data : data.children || [];
        const normalized = list.map((c) => {
          const photoUrl = normalizePhoto(c);
          return {
            ...c,
            photoUrl,
            reintegration: c.reintegration || c.reintegration_details || null,
          };
        });

        setChildren(normalized);
      } catch (err) {
        console.error("Fetch children error:", err);
      }
    };

    if (token) fetchChildren();
  }, [API_URL, token, normalizePhoto]);

  useEffect(() => {
    const openId = searchParams.get("open");
    const devId = searchParams.get("dev");

    if (!children?.length) return;

    if (openId) {
      const idNum = Number(openId);
      const found = children.find((c) => Number(c.id) === idNum);
      if (found) {
        navigate(`/children/${idNum}`, { replace: true });
        return;
      }
    }

    if (devId) {
      const idNum = Number(devId);
      const found = children.find((c) => Number(c.id) === idNum);
      if (found) {
        setDevChild(withFullName(found));
        navigate("/children", { replace: true });
        return;
      }
    }
  }, [searchParams, children, navigate]);

  useEffect(() => {
    if (searchParams.get("add") === "1") {
      setShowModal(true);
      navigate(CHILDREN_ROUTE, { replace: true });
    }
  }, [searchParams, navigate]);

  const handleAddChild = async (child) => {
    const job = async () => {
      const formData = new FormData();

      Object.entries(child || {}).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (key === "photo") return;
        if (key === "photoUrl") return;
        if (key === "reintegration") return;
        formData.append(key, value);
      });

      if (child?.photo instanceof File) formData.append("photo", child.photo);

      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: formData,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(getErrMsg(data, "Failed to add child."));
      }

      const created = data.child || data;
      const photoUrl = normalizePhoto(created);

      const normalized = {
        ...created,
        photoUrl,
        reintegration: created.reintegration || created.reintegration_details || null,
      };

      setChildren((prev) => [normalized, ...prev]);
      setShowModal(false);
    };

    try {
      await toast.promise(job(), {
        loading: "Creating child record...",
        success: "Child record created successfully!",
        error: (e) => e?.message || "Failed to add child.",
      });
    } catch (err) {
      console.error("Add child error:", err);
    }
  };

  const updateChild = async (updatedChild) => {
    const job = async () => {
      const id = updatedChild?.id;
      if (!id) throw new Error("Missing child ID.");

      const formData = new FormData();

      Object.entries(updatedChild || {}).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (key === "photo") return;
        if (key === "photoUrl") return;
        if (key === "reintegration") return;
        formData.append(key, value);
      });

      if (updatedChild?.photo instanceof File) formData.append("photo", updatedChild.photo);

      const res = await fetch(`${API_URL}/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: formData,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(getErrMsg(data, "Failed to update child."));
      }

      const saved = data.child || data;

      const photoUrl =
        normalizePhoto(saved) ||
        normalizePhoto(updatedChild) ||
        (updatedChild?.photoUrl ? normalizePhoto({ photoUrl: updatedChild.photoUrl }) : null);

      const normalized = {
        ...saved,
        photoUrl,
        reintegration: saved.reintegration ?? saved.reintegration_details ?? updatedChild.reintegration ?? null,
      };

      setChildren((prev) => prev.map((c) => (c.id === normalized.id ? normalized : c)));
      setEditChild((prev) => (prev?.id === normalized.id ? normalized : prev));
    };

    try {
      await toast.promise(job(), {
        loading: "Saving changes...",
        success: "Child profile updated successfully!",
        error: (e) => e?.message || "Failed to update child.",
      });
    } catch (err) {
      console.error("Update child error:", err);
    }
  };

  const saveReintegration = async (childId, reintegrationPayload) => {
    const job = async () => {
      const res = await fetch(`${API_URL}/${childId}/reintegration`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(reintegrationPayload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(getErrMsg(data, "Failed to save reintegration."));
      }

      const saved = data.child || data;
      const photoUrl = normalizePhoto(saved);

      const normalized = {
        ...saved,
        photoUrl,
        reintegration: saved.reintegration ?? saved.reintegration_details ?? null,
      };

      setChildren((prev) => prev.map((c) => (c.id === normalized.id ? normalized : c)));
      setReintegrationChild(null);
    };

    try {
      await toast.promise(job(), {
        loading: "Saving reintegration...",
        success: "Reintegration saved successfully!",
        error: (e) => e?.message || "Failed to save reintegration.",
      });
    } catch (err) {
      console.error("Save reintegration error:", err);
    }
  };

  const filteredChildren = useMemo(() => {
    return children.filter((child) => {
      const fullText = `
        ${child.firstName || child.first_name || ""} ${child.middleName || child.middle_name || ""} ${
        child.lastName || child.last_name || ""
      }
        ${child.house || ""} ${child.educationLevel || child.education_level || ""}
      `
        .toLowerCase()
        .trim();

      return fullText.includes(search.toLowerCase());
    });
  }, [children, search]);

  const withFullName = (c) => {
    if (!c) return null;
    const firstName = c.firstName ?? c.first_name ?? "";
    const middleName = c.middleName ?? c.middle_name ?? "";
    const lastName = c.lastName ?? c.last_name ?? "";
    return {
      ...c,
      fullName: `${firstName} ${middleName ? middleName + " " : ""}${lastName}`.trim(),
    };
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-950 min-h-screen space-y-6 transition-colors duration-300">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Children Information
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Manage child profiles, status, and reintegration details
          </p>
        </div>

        <Button
          onClick={() => setShowModal(true)}
          variant="primary"
          type="button"
          className="w-full sm:w-[140px] h-[44px] rounded-xl shadow-sm"
        >
          <div className="flex items-center justify-center gap-2">
            <span className="text-sm font-semibold">Add Child</span>
          </div>
        </Button>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 p-4 transition-colors duration-300">
        <div className="relative">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
            size={18}
          />
          <input
            type="text"
            placeholder="Search children by name, home, or education level..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-11 pr-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none focus:ring-2 focus:ring-blue-500/20"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-6">
        {filteredChildren.map((child) => {
          const firstName = child.firstName ?? child.first_name ?? "";
          const middleName = child.middleName ?? child.middle_name ?? "";
          const lastName = child.lastName ?? child.last_name ?? "";
          const fullName = `${firstName} ${middleName ? middleName + " " : ""}${lastName}`.trim();

          const dateOfBirth = child.dateOfBirth ?? child.date_of_birth ?? "";
          const age = calculateAge(dateOfBirth);

          const isReintegrated =
            String(child.status || "").toLowerCase() === "reintegrated" || !!child.reintegration;

          const reintegrationName =
            child?.reintegration?.guardianName ||
            child?.reintegration?.reintegratedWith ||
            child?.reintegration?.parentName ||
            "";

          return (
            <div
              key={child.id}
              className="bg-white dark:bg-gray-900 rounded-3xl shadow-sm border border-gray-200 dark:border-gray-800 p-5 transition-colors duration-300"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <ChildAvatar fullName={fullName} photoUrl={child.photoUrl} />
                  <div className="min-w-0">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">{fullName}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {age !== "" ? `${age} years old` : "Age unavailable"} • {child.gender || "—"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => navigate(`/children/${child.id}`, { state: { child } })}
                    className="w-10 h-10 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                    title="View"
                  >
                    <Eye size={16} />
                  </button>

                  <button
                    type="button"
                    onClick={() => setEditChild(child)}
                    className="w-10 h-10 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                    title="Edit"
                  >
                    <Pencil size={16} />
                  </button>

                  <button
                    type="button"
                    onClick={() => setReintegrationChild(child)}
                    className="w-10 h-10 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                    title="Reintegration"
                  >
                    <UserPlus size={16} />
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {child.status && (
                  <span className={`px-3 py-1 rounded-full border text-sm ${getStatusColor(child.status)}`}>
                    {child.status}
                  </span>
                )}
                {child.healthStatus && (
                  <span
                    className={`px-3 py-1 rounded-full border text-sm ${getHealthStatusColor(child.healthStatus)}`}
                  >
                    {child.healthStatus}
                  </span>
                )}
                {child.adoptionStatus && (
                  <span
                    className={`px-3 py-1 rounded-full border text-sm ${getAdoptionStatusColor(child.adoptionStatus)}`}
                  >
                    {child.adoptionStatus}
                  </span>
                )}
              </div>

              <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <p className="flex items-center gap-2">
                  <MapPin size={14} className="text-gray-400 dark:text-gray-500" />
                  {child.house || "No house assigned"}
                </p>
                <p className="flex items-center gap-2">
                  <User size={14} className="text-gray-400 dark:text-gray-500" />
                  {child.houseParent || child.house_parent || "No house parent"}
                </p>
                <p className="flex items-center gap-2">
                  <Calendar size={14} className="text-gray-400 dark:text-gray-500" />
                  <span>Birth Date: {dateOfBirth ? formatShortDate(dateOfBirth) : "—"}</span>
                </p>
                <p className="flex items-center gap-2">
                  <Calendar size={14} className="text-gray-400 dark:text-gray-500" />
                  <span>
                    Admitted: {child.admissionDate || child.admission_date ? formatShortDate(child.admissionDate || child.admission_date) : "—"}
                  </span>
                </p>
                <p className="flex items-center gap-2">
                  <FileText size={14} className="text-gray-400 dark:text-gray-500" />
                  <span>Education: {child.educationLevel || child.education_level || "—"}</span>
                </p>
                <p className="flex items-center gap-2">
                  <Heart size={14} className="text-gray-400 dark:text-gray-500" />
                  <span>Health: {child.healthStatus || "—"}</span>
                </p>

                {isReintegrated && (
                  <p className="flex items-center gap-2">
                    <Home size={14} className="text-green-600 dark:text-green-400" />
                    <span className="text-green-700 dark:text-green-300 font-medium">
                      Reintegrated with {reintegrationName || "—"}
                    </span>
                  </p>
                )}
              </div>

              {child.notes && (
                <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-xl text-sm text-gray-700 dark:text-gray-200 transition-colors duration-300">
                  {child.notes}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showModal && <AddChildModal onClose={() => setShowModal(false)} onAddChild={handleAddChild} />}

      {editChild && <EditProfileModal child={editChild} onClose={() => setEditChild(null)} onSave={updateChild} />}

      {reintegrationChild && (
        <ReintegrationModal
          child={reintegrationChild}
          onClose={() => setReintegrationChild(null)}
          onComplete={(payload) => saveReintegration(reintegrationChild.id, payload.reintegration)}
        />
      )}

      {devChild && <DevelopmentSummaryModal child={devChild} token={token} onClose={() => setDevChild(null)} />}
    </div>
  );
};

export default Children;